import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMenuPosition } from '../StatusDropdown/hooks/useMenuPosition';
import { chooseOnPress, commitAfterPaint, hideMenuNow } from '../../../shared/StatusDropdown/hooks/paintChoice';

import './styles/priority.css';

/**
 * Priority options with their colors
 */
export const PRIORITY_OPTIONS = [
  { id: 'high', name: 'High', color: '#ef4444' },
  { id: 'medium', name: 'Medium', color: '#f59e0b' },
  { id: 'low', name: 'Low', color: '#22c55e' },
  { id: null, name: 'None', color: '#6b7280' },
];

/**
 * Get priority config by ID
 */
export const getPriorityConfig = (priorityId) => {
  return PRIORITY_OPTIONS.find(p => p.id === priorityId) || PRIORITY_OPTIONS[3]; // Default to 'None'
};

/**
 * PriorityDropdown Component
 * 
 * Dropdown for selecting task priority.
 * Uses portal to render menu outside the transformed canvas.
 */
const PriorityDropdown = ({
  taskId,
  currentPriority,
  onPriorityChange,
  showLabel = true,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Menu positioning hook (reuse from StatusDropdown)
  const { menuPosition, openMenu } = useMenuPosition(btnRef, menuRef, showMenu);

  // Get current priority config
  const priorityConfig = getPriorityConfig(currentPriority);

  // Close menu when clicking outside
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setShowMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [showMenu]);

  // Close menu when canvas starts panning (trackpad or mouse drag)
  useEffect(() => {
    if (!showMenu) return;

    const handlePanStart = () => setShowMenu(false);
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [showMenu]);

  const handleClick = (e) => {
    e.stopPropagation();
    
    if (!showMenu) {
      openMenu(e);
    }
    
    setShowMenu(!showMenu);
  };

  const handleSelect = useCallback((priorityId) => {
    const option = getPriorityConfig(priorityId);
    hideMenuNow(menuRef.current);
    const button = btnRef.current;
    if (button) {
      button.style.setProperty('--priority-color', option.color);
      button.style.setProperty('--priority-bg', `${option.color}15`);
      const label = button.querySelector('.priority-dropdown__label');
      const dot = button.querySelector('.priority-dropdown__dot');
      if (label) label.textContent = option.name;
      if (dot) dot.style.background = option.color;
    }
    commitAfterPaint(() => {
      onPriorityChange?.(taskId, priorityId);
      setShowMenu(false);
    });
  }, [onPriorityChange, taskId]);

  // Menu content with portal
  const menuContent = showMenu && (
    <div 
      ref={menuRef}
      className="priority-dropdown__menu"
      style={{ 
        position: 'fixed',
        top: menuPosition.top, 
        left: menuPosition.left,
        zIndex: 10000,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {PRIORITY_OPTIONS.map(option => (
        <button
          key={option.id ?? 'none'}
          className={`priority-dropdown__option ${option.id === currentPriority ? 'priority-dropdown__option--active' : ''}`}
          onMouseDown={(event) => chooseOnPress(event, () => handleSelect(option.id))}
        >
          <span 
            className="priority-dropdown__option-dot" 
            style={{ background: option.color }}
          />
          <span className="priority-dropdown__option-label">{option.name}</span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <div className="priority-dropdown__row">
        {showLabel && <span className="priority-dropdown__field-label">Priority</span>}
        <button 
          ref={btnRef}
          className="priority-dropdown__btn"
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            '--priority-color': priorityConfig.color,
            '--priority-bg': `${priorityConfig.color}15`,
          }}
        >
          <span 
            className="priority-dropdown__dot" 
            style={{ background: priorityConfig.color }}
          />
          <span className="priority-dropdown__label">
            {priorityConfig.name}
          </span>
          <span className="priority-dropdown__arrow">▾</span>
        </button>
      </div>

      {showMenu && createPortal(menuContent, document.body)}
    </>
  );
};

export default PriorityDropdown;
